import {
  FileTextIcon,
  PanelLeftOpenIcon,
  ThumbsDown,
  ThumbsUpIcon,
} from "lucide-react";
import { SourceItem, SourceItemVariant } from "../citation/sourceItem";
import { Citation } from "@/constants/citation.constant";
import clsx from "clsx";
import {
  FEEDBACK_SIDEBAR_WIDTH,
  MessageFeedbackType,
} from "@/constants/chat.constant";
import { ChatMessageList } from "@/interfaces/chat.interface";
import { useState } from "react";
import ChatMessageModal from "../chat/chat-message-modal";
import { FileAttachmentWrapper } from "../chat/attachement-wrapper";

/**
 * Props for the ConversationSidebar component
 */
interface ConversationSidebarProps {
  /** Message list data to display */
  message: ChatMessageList;
  /** Callback fired when sidebar is closed */
  onClose: () => void;
  /** Callback fired when citation source is opened */
  onOpenSources: (citation: Citation) => void;
  /** Whether citation is loading */
  isCitationLoading: boolean;
  /** ID of selected citation */
  selectedCitationId?: number | null;
  /** object like MESSAGE_FEEDBACK from "@/constants/chat.constant" */
  messageFeedback: any
}

/**
 * ConversationSidebar component displays a right-side sidebar with conversation
 * details including message content, feedback, sources, and attachments. Opens
 * modal for full message view. Shows file attachments and citation sources.
 * 
 * @component
 * @param {ConversationSidebarProps} props - Component props
 * @param {ChatMessageList} props.message - Message list data
 * @param {() => void} props.onClose - Close callback
 * @param {(citation: Citation) => void} props.onOpenSources - Open sources callback
 * @param {boolean} props.isCitationLoading - Whether citation loading
 * @param {number | null} [props.selectedCitationId] - Selected citation ID
 * @returns {JSX.Element} Right-side sidebar with conversation details
 * 
 * @example
 * ```tsx
 * <ConversationSidebar
 *   message={message}
 *   onClose={handleClose}
 *   onOpenSources={handleOpenSources}
 *   isCitationLoading={isLoading}
 * />
 * ```
 */
export default function ConversationSidebar({
  message,
  onClose,
  onOpenSources,
  isCitationLoading,
  selectedCitationId,
  messageFeedback,
}: ConversationSidebarProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // modal content + title
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  // open modal with dynamic content
  const openModalWithContent = (content: string, title: string) => {
    setModalContent(content);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      {/* Sidebar Content */}
      <div
        className={`flex flex-col border-l h-[calc(100svh)]
         ${FEEDBACK_SIDEBAR_WIDTH.md} ${FEEDBACK_SIDEBAR_WIDTH.lg} ${FEEDBACK_SIDEBAR_WIDTH.xl}
        border-sidebar-border_bold bg-background px-2`}
      >
        <div className="py-4 bg-background flex justify-between items-center px-4 ">
          <div className="text-base text-text-500">Conversation snapshot</div>
          <div className="hover:bg-accent p-[5px] rounded">
            <PanelLeftOpenIcon
              size={16}
              className="text-text-200 cursor-pointer"
              onClick={onClose}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6 custom-scrollbar">
          {/* Feedback Section */}
          <div className="border border-border_chat rounded-lg p-4">
            <div className="text-text-500 text-sm flex items-center gap-1.5 pb-3">
              <ThumbsUpIcon
                size={16}
                className={clsx(
                  "text-slate-500",
                  message.feedback?.feedback_type ===
                    MessageFeedbackType.POSITIVE
                    ? "fill-primary-500"
                    : ""
                )}
              />
              <ThumbsDown
                size={16}
                className={clsx(
                  "text-slate-500",
                  message.feedback?.feedback_type ===
                    MessageFeedbackType.NEGATIVE
                    ? "fill-primary-500"
                    : ""
                )}
              />
              <div className="pl-1">Feedback</div>
            </div>

            {messageFeedback[
              message.feedback.feedback_type as MessageFeedbackType
            ]?.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pb-3">
                {messageFeedback[
                  message.feedback.feedback_type as MessageFeedbackType
                ]?.tags.map((tag: any) => (
                  <div
                    key={tag}
                    className={clsx(
                      "px-2 py-1 rounded-full md:text-sm text-xxs font-medium transition-all duration-200",
                      message.feedback.feedback_text?.tags?.includes(tag)
                        ? "bg-primary-100 text-primary-500 border-2 border-primary-500"
                        : "text-[#3A3A3A] border-2 border-[#AFB4C5]"
                    )}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}

            <div className="bg-muted text-text-200 text-sm rounded-md p-3 border border-border_bold">
              {message.feedback.feedback_text?.message || "NIL"}
            </div>
          </div>

          {/* User Prompt Section */}
          <div className="border border-border_chat rounded-lg p-4">
            <div className="text-text-500 text-sm flex items-center gap-2 mb-2">
              <FileTextIcon size={16} />
              User Prompt
            </div>
            <div className="relative bg-muted text-text-200 text-sm rounded-md border border-border_bold p-3 overflow-hidden">
              <p className="line-clamp-4 leading-relaxed">{message.prompt}</p>
            </div>

            {message.uploads && message.uploads.length > 0 ? (
              <div className="py-3 flex items-start gap-4 md:gap-6 min-w-0 relative">
                {<FileAttachmentWrapper isInput={true} uploadList={message.uploads?.map((upload, index) => ({ id: `upload-${upload.id}-${index}`, upload, showRemove: false, showDownload: true }))} />}
              </div>
            ) : null}

            {/*button opens prompt in modal */}
            <button
              onClick={() =>
                openModalWithContent(message.prompt ?? "", "User Prompt")
              }
              className="w-full text-sm mt-3 py-1 rounded-md font-medium bg-primary-100 text-primary-500 hover:bg-primary-100/80 transition"
            >
              See complete prompt
            </button>
          </div>

          {/* Assistant Response Section */}
          <div className="border border-border_chat rounded-lg p-4">
            <div className="text-text-500 text-sm flex items-center gap-2 mb-2">
              <FileTextIcon size={16} />
              Assistant Response
            </div>
            <div className="relative bg-muted text-text-200 text-sm rounded-md border border-border_bold p-3 overflow-hidden">
              <p className="line-clamp-4 leading-relaxed">{message.response}</p>
            </div>

            {/*button opens response in modal */}
            <button
              onClick={() =>
                openModalWithContent(
                  message.response ?? "",
                  "Assistant Response"
                )
              }
              className="w-full text-sm mt-3 py-1 rounded-md font-medium bg-primary-100 text-primary-500 hover:bg-primary-100/80 transition"
            >
              See complete response
            </button>
          </div>

          {/* Sources */}
          {message.citations && message.citations.length > 0 && (
            <div className="border border-border_chat rounded-lg p-4">
              <div className="flex flex-row items-center text-text-500 gap-2 text-sm pb-4">
                <FileTextIcon size={16} />
                <div className="text-text-500">Sources</div>
              </div>

              <div className="space-y-4">
                {message.citations.map((source: Citation, index: number) => (
                  <SourceItem
                    key={source.id || index}
                    source={{ ...source, content: source.content_snippet }}
                    index={index + 1}
                    onClick={onOpenSources}
                    isCitationLoading={isCitationLoading}
                    selectedCitationId={selectedCitationId}
                    variant={SourceItemVariant.COMPACT}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/*Modal shows prompt OR response */}
      <ChatMessageModal
        open={isModalOpen}
        onClose={handleCloseModal}
        message={modalContent}
        title={modalTitle}
      />
    </>
  );
}
